import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Chip,
  Button,
  Badge
} from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import StatusBadge from './StatusBadge';

function StationeryOrdersTab({ orders, loading, error, onOrderClick, getTotalNoteCount, handleOpenNotesDialog }) {
  // Add debug logging
  

  
  // Helper function to extract items safely for stationery orders
  const getItems = (order) => {
    // For stationery orders, create an item from the order data itself
    if (order.productType && order.quantity) {
      return [{
        name: order.productType,
        quantity: order.quantity,
        price: order.price || 0
      }];
    }
    
    // If order has items array, use it (for other order types)
    if (order.items && Array.isArray(order.items)) {
      return order.items;
    }
    
    // Default empty array
    return [];
  };
  
  // Get a summary of items for display
  const getItemsSummary = (order) => {
    const items = getItems(order);
    if (items.length === 0) {
      return 'No items';
    }
    
    // Return just the count if there are many items
    if (items.length > 2) {
      return `${items.length} items`;
    }
    
    // Return the names for a small number of items
    return items.map(item => `${item.name} (${item.quantity || 1})`).join(', ');
  };
  
  // Calculate total value
  const getTotalValue = (order) => {
    // For stationery orders, use totalPrice field
    if (order.totalPrice) {
      return parseFloat(order.totalPrice || 0).toFixed(2);
    }
    
    // Fallback to value field
    if (order.value) {
      return parseFloat(order.value || 0).toFixed(2);
    }
    
    // Try to calculate from items
    const items = getItems(order);
    let total = 0;
    items.forEach(item => {
      const price = parseFloat(item.price || 0);
      const quantity = parseInt(item.quantity || 1, 10);
      total += price * quantity;
    });
    
    return total.toFixed(2);
  };

  // Handle order click to view details
  const handleOrderClick = (order) => {
    if (onOrderClick) {
      onOrderClick(order);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography color="error">Error loading orders: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {orders.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="h6" color="textSecondary">
            No stationery orders found
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell>Product Type</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Total Price</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow
                  key={order.id}
                  hover
                  onClick={() => handleOrderClick(order)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    {order.id}
                  </TableCell>
                  <TableCell>
                    {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {order.organizationName || order.businessName || 'N/A'}
                  </TableCell>
                  <TableCell>
                    {order.productType || 'N/A'}
                  </TableCell>
                  <TableCell>
                    {order.quantity || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={order.status || 'New'} />
                  </TableCell>
                  <TableCell>
                    ${getTotalValue(order)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        const existingNotes = [];
                        if (order.notes) {
                          existingNotes.push({
                            id: 'general',
                            content: order.notes,
                            createdBy: 'Order Team',
                            createdAt: order.createdAt || new Date().toISOString(),
                            isNew: false,
                            noteType: 'general'
                          });
                        }
                        if (order.designNotes) {
                          existingNotes.push({
                            id: 'design',
                            content: order.designNotes,
                            createdBy: 'Design Team',
                            createdAt: order.createdAt || new Date().toISOString(),
                            isNew: false,
                            noteType: 'design'
                          });
                        }
                        handleOpenNotesDialog('order', order.id.toString(), `Order #${order.id} - ${order.organizationName || order.businessName || 'Order'}`, existingNotes);
                      }}
                      sx={{ 
                        minWidth: 'auto',
                        px: 1,
                        py: 0.5,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        borderRadius: 1,
                        color: (() => {
                          const totalCount = getTotalNoteCount ? getTotalNoteCount(order, 'order') : 0;
                          return totalCount > 0 ? 'primary.main' : 'text.secondary';
                        })(),
                        '&:hover': {
                          bgcolor: 'action.hover',
                        }
                      }}
                    >
                      Notes
                      {(() => {
                        const totalCount = getTotalNoteCount ? getTotalNoteCount(order, 'order') : 0;
                        return totalCount > 0 ? (
                          <Box
                            component="span"
                            sx={{
                              ml: 0.5,
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              px: 0.5,
                              py: 0.1,
                              borderRadius: '50%',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              minWidth: 18,
                              height: 18,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {totalCount}
                          </Box>
                        ) : null;
                      })()}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

StationeryOrdersTab.propTypes = {
  orders: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onOrderClick: PropTypes.func,
  getTotalNoteCount: PropTypes.func,
  handleOpenNotesDialog: PropTypes.func
};

export default StationeryOrdersTab; 